import PropTypes from 'prop-types';

import Iconify from 'src/components/Iconify';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import { Switch } from 'src/@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';
import {
    formatarCanaisParaSelect,
    filtrarCanaisCampanha,
    canalPodeDisparar,
    motivoBloqueioDisparo,
} from 'src/utils/canaisDisparo';

import { CAMPANHAS_DESIGN as D } from '../../Campanhas.design';

const TRANSPORTES = [
    {
        valor: 'whatsapp',
        label: 'WhatsApp',
        descricao: 'Texto, mídia, áudio e templates. Exige um canal conectado.',
        icon: 'mdi:whatsapp',
        cor: 'bg-green-50 dark:bg-green-500/10 text-green-600 dark:text-green-400',
    },
    {
        valor: 'sms',
        label: 'SMS',
        descricao: 'Texto simples. Chega em qualquer celular, sem precisar de canal.',
        icon: 'mdi:message-text-outline',
        cor: 'bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400',
    },
];

export default function PassoIdentificacao({ form, set, setTipoEnvio, podeUsarSms, canais, isMultisession, isEdit }) {
    const ehSms = form.tipoEnvio === 'sms';

    const transportes = podeUsarSms || (isEdit && form.tipoEnvio === 'sms')
        ? TRANSPORTES
        : TRANSPORTES.filter((t) => t.valor !== 'sms');

    // Para SMS nao existe canal: a credencial da Comtele e global. O select vira
    // apenas a escolha de qual conta e dona dos contatos, entao nenhuma opcao fica
    // desabilitada por capacidade de disparo.
    const canaisElegiveis = filtrarCanaisCampanha(canais);

    const opcoesCanal = ehSms
        ? canaisElegiveis.map((c) => ({ value: c.cliente_id || c.clienteId, label: c.nome, disabled: false }))
        : formatarCanaisParaSelect(canaisElegiveis) || [];

    const canalSelecionado = canaisElegiveis.find((c) => (c.cliente_id || c.clienteId) === form.canalEnvio);
    const canalBloqueado = !ehSms && canalSelecionado && !canalPodeDisparar(canalSelecionado.platform);
    const riscoBanimento = !ehSms && canalSelecionado?.platform === 'whatsapp';

    return (
        <div className="space-y-4">
            <div className={D.wizard.secao}>
                <Label className={D.wizard.label}>Nome da campanha</Label>
                <Input
                    value={form.nome}
                    onChange={(e) => set('nome', e.target.value)}
                    placeholder="Ex.: Promoção de fim de ano"
                    className={D.wizard.input}
                />
                <p className={D.wizard.secaoSub}>Só aparece para a sua equipe — o cliente não vê este nome.</p>
            </div>

            <div className={D.wizard.secao}>
                <p className={D.wizard.secaoTitulo}>Como enviar?</p>
                <p className={D.wizard.secaoSub}>O canal define o que você pode colocar na mensagem.</p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                    {transportes.map((t) => {
                        const ativo = form.tipoEnvio === t.valor;
                        return (
                            <button
                                key={t.valor}
                                type="button"
                                disabled={isEdit}
                                onClick={() => setTipoEnvio(t.valor)}
                                className={`${ativo ? D.wizard.cardOpcaoAtiva : D.wizard.cardOpcao} ${isEdit ? 'opacity-60 cursor-not-allowed' : ''}`}
                            >
                                <div className={`${D.wizard.cardIcone} ${t.cor}`}>
                                    <Iconify icon={t.icon} width={18} />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-sm font-semibold text-gray-900 dark:text-[#D1D2D3]">{t.label}</p>
                                    <p className="text-[11px] text-gray-500 dark:text-[#ABABAD] leading-snug mt-0.5">
                                        {t.descricao}
                                    </p>
                                </div>
                            </button>
                        );
                    })}
                </div>

                {isEdit && (
                    <div className={`${D.wizard.avisoInfo} mt-3`}>
                        <Iconify icon="mdi:information-outline" width={14} className="flex-shrink-0 mt-px" />
                        <span>O canal de envio não pode ser alterado depois que a campanha foi criada.</span>
                    </div>
                )}
            </div>

            {isMultisession && (
                <div className={D.wizard.secao}>
                    <Label className={D.wizard.label}>
                        {ehSms ? 'Conta / origem dos contatos' : 'Canal de envio'}
                    </Label>
                    <Select value={form.canalEnvio} onValueChange={(v) => set('canalEnvio', v)}>
                        <SelectTrigger className={D.wizard.input}>
                            <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                            {opcoesCanal.map((o) => (
                                <SelectItem key={o.value} value={o.value} disabled={o.disabled}>
                                    {o.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    {ehSms && (
                        <p className={D.wizard.secaoSub}>
                            O SMS não usa canal conectado — esta escolha define de qual conta virão os contatos.
                        </p>
                    )}

                    {canalBloqueado && (
                        <div className={`${D.wizard.avisoErro} mt-3`}>
                            <Iconify icon="mdi:alert-circle-outline" width={14} className="flex-shrink-0 mt-px" />
                            <span>{motivoBloqueioDisparo(canalSelecionado.platform)}</span>
                        </div>
                    )}

                    {riscoBanimento && (
                        <div className={`${D.wizard.avisoAlerta} mt-3`}>
                            <Iconify icon="mdi:alert-outline" width={14} className="flex-shrink-0 mt-px" />
                            <span>
                                <strong>Alto risco de banimento.</strong> Disparos em massa por WhatsApp não-oficial
                                podem derrubar o número. Prefira um canal oficial (WABA) ou SMS.
                            </span>
                        </div>
                    )}
                </div>
            )}

            {!ehSms && (
                <div className={D.wizard.secao}>
                    <div className="flex items-center justify-between gap-4">
                        <div className="min-w-0">
                            <p className={D.wizard.secaoTitulo}>Abrir atendimento</p>
                            <p className={D.wizard.secaoSub}>
                                Cria uma conversa na fila quando o contato responder.
                            </p>
                        </div>
                        <Switch
                            checked={form.isChatAtendimento}
                            onCheckedChange={(v) => set('isChatAtendimento', v)}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

PassoIdentificacao.propTypes = {
    form: PropTypes.object.isRequired,
    set: PropTypes.func.isRequired,
    setTipoEnvio: PropTypes.func.isRequired,
    podeUsarSms: PropTypes.bool,
    canais: PropTypes.array,
    isMultisession: PropTypes.bool,
    isEdit: PropTypes.bool,
};
